/**
 * Photos live in src/assets/photos and are discovered at build time, so a slot
 * with no file renders the club-lit stand-in without requesting anything (no
 * 403s in the console, no layout shift). Drop a file in with the slot name
 * and it appears on the next build.
 */
const PHOTOS = import.meta.glob('../assets/photos/*.{jpg,jpeg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

export function photoUrl(name: string): string | undefined {
  const hit = Object.entries(PHOTOS).find(([path]) => path.endsWith(`/${name}`));
  return hit?.[1];
}

/**
 * The Photos page is the gallery folder, in file-name order: every file in
 * src/assets/photos/gallery is a photo, and the same name under gallery/thumbs
 * (when present) is the grid tile for it. Number the names (01-, 02-, ...) to
 * choose the order; add or remove files and the page follows on the next build.
 */
const GALLERY = import.meta.glob('../assets/photos/gallery/*.{jpg,jpeg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const GALLERY_THUMBS = import.meta.glob('../assets/photos/gallery/thumbs/*.{jpg,jpeg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

export interface GalleryPhoto {
  /** File name, e.g. `12-white-guitar.jpg`; the caption key. */
  name: string;
  url: string;
  thumb: string;
}

function baseName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

export function galleryPhotos(): GalleryPhoto[] {
  const thumbs = new Map(Object.entries(GALLERY_THUMBS).map(([p, url]) => [baseName(p), url]));
  return Object.entries(GALLERY)
    .map(([path, url]) => {
      const name = baseName(path);
      return { name, url, thumb: thumbs.get(name) ?? url };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }));
}
